import { zodResolver } from "@hookform/resolvers/zod";
import type { Result } from "@zxing/library";
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MinusIcon,
  ScanLineIcon,
  SearchIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import type { z } from "zod";
import { SubmitButton } from "@/components/buttons/submit-button";
import { ScannerModal } from "@/components/modals/scanner-modal";
import { Button } from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import {
  Timeline,
  TimelineContent,
  TimelineHeader,
  TimelineIndicator,
  TimelineItem,
  TimelineSeparator,
  TimelineTitle,
} from "@/components/ui/timeline";
import { useDebounce } from "@/hooks/use-debounce";
import { useSession } from "@/hooks/use-session";
import {
  useBookMSISDNMutation,
  useLazyGetAvailableMSISDNsQuery,
} from "@/redux/services/order-portal";
import { setOrder } from "@/redux/slices/order";
import { useAppDispatch, useAppSelector } from "@/redux/store";
import { selectNumberSchema } from "@/shared/schemas/select-number";

export const SelectNumberForm = ({
  serviceType,
  className,
}: {
  serviceType: ServiceType;
  className?: string;
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { session } = useSession();
  // const isProduction = import.meta.env.VITE_ENV === "production";

  const [isMsisdnNumberCommandOpen, setIsMsisdnNumberCommandOpen] =
    useState<boolean>(false);
  const [msisdnNumberQuery, setMsisdnNumberQuery] = useState<string>("");
  const [isScannerOpen, setIsScannerOpen] = useState<boolean>(false);
  const [stopStream, setStopStream] = useState<boolean>(false);

  const order = useAppSelector((state) => state.order);
  const dispatch = useAppDispatch();
  const [
    getAvailableMSISDNs,
    { data: availableMSISDNs, currentData: currentAvailableMSISDNs },
  ] = useLazyGetAvailableMSISDNsQuery();
  const [bookMSISDN] = useBookMSISDNMutation();

  const form = useForm<z.infer<typeof selectNumberSchema>>({
    resolver: zodResolver(selectNumberSchema),
    defaultValues: {
      isAuthenticated: !!session,
      msisdnId: order.msisdnId ?? "",
      msisdnNumber: order.msisdnNumber ?? "",
      imsiNumber: session ? (order.imsiNumber ?? "") : undefined,
    },
  });

  useEffect(() => {
    getAvailableMSISDNs({
      // customer_type should be non-null because of ProtectedRoute
      customer_type: order.customerType as CustomerType,
      category: "Recycled",
      sim_no: order.msisdnNumber ?? "",
    });
  }, []);

  useDebounce(
    () => {
      if (msisdnNumberQuery.length === 0 || msisdnNumberQuery.length > 1) {
        getAvailableMSISDNs({
          // customer_type should be non-null because of ProtectedRoute
          customer_type: order.customerType as CustomerType,
          category: "Recycled",
          sim_no: msisdnNumberQuery,
        });
      } else {
        toast.info("Please enter at least 2 digits");
      }
    },
    1000,
    [msisdnNumberQuery]
  );

  function getSelectedMSISDN(selectedMsisdnNumber: string) {
    const selectedMSISDN =
      currentAvailableMSISDNs?.find(
        (msisdn) => msisdn.msisdnNo === selectedMsisdnNumber
      ) ||
      availableMSISDNs?.find(
        (msisdn) => msisdn.msisdnNo === selectedMsisdnNumber
      );

    return selectedMSISDN;
  }

  const handleScanResult = (error: unknown, result?: Result) => {
    if (result) {
      const scannedText = result.getText() || "";
      form.setValue("imsiNumber", scannedText);
      closeScannerModal();
      toast.success(t("scanner.messages.scan-success"));
    }
    if (error) {
      toast.error(t("scanner.messages.scanner-error"));
    }
  };

  const handleScanError = (error: string | DOMException) => {
    if (typeof error !== "string" && error.name === "NotAllowedError") {
      toast.error(t("scanner.messages.camera-permission-denied"));
    } else {
      toast.error(t("scanner.messages.scanner-error"));
    }
  };

  const closeScannerModal = () => {
    // Stop the stream before closing to prevent browser freeze
    setStopStream(true);
    setTimeout(() => {
      setIsScannerOpen(false);
      setStopStream(false);
    }, 100);
  };

  const onSubmit: SubmitHandler<z.infer<typeof selectNumberSchema>> = async (
    data
  ) => {
    try {
      const updatedOrder = {
        ...order,
        imsiNumber: data.imsiNumber,
      };

      if (!order.msisdnNumber || order.msisdnNumber !== data.msisdnNumber) {
        // if (isProduction) {
        const bookMSISDNResponse = await bookMSISDN({
          // customer_type should be non-null because of ProtectedRoute
          customer_type: order.customerType as CustomerType,
          msisdnID: data.msisdnId,
          msisdnNo: data.msisdnNumber,
        });
        if (bookMSISDNResponse.error || !bookMSISDNResponse.data) {
          throw new Error("Failed to book service number");
        }
        // }

        updatedOrder.msisdnId = data.msisdnId;
        updatedOrder.msisdnNumber = data.msisdnNumber;
      }

      dispatch(setOrder(updatedOrder));

      navigate(`/${serviceType}/customer-information`);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "An unknown error occurred"
      );
    }
  };

  return (
    <Form {...form}>
      <form className={className} onSubmit={form.handleSubmit(onSubmit)}>
        <Timeline defaultValue={1}>
          <TimelineItem step={1}>
            <TimelineHeader>
              <TimelineSeparator className="group-data-[orientation=vertical]/timeline:h-[calc(100%-1.5rem-0.25rem)] group-data-[orientation=vertical]/timeline:translate-y-6.5" />
              <TimelineTitle className="-mt-1">
                {t("select-number.title")}
              </TimelineTitle>
              <TimelineIndicator className="flex size-6 items-center justify-center">
                <MinusIcon className="size-4" />
              </TimelineIndicator>
            </TimelineHeader>
            <TimelineContent className="flex flex-col gap-4">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
                <p className="text-base">{order.plan.name}</p>

                <FormField
                  control={form.control}
                  name="msisdnNumber"
                  render={({ field }) => {
                    const selectedMSISDN = getSelectedMSISDN(field.value);
                    const displayValue = selectedMSISDN
                      ? selectedMSISDN.msisdnNo
                      : field.value || "";

                    return (
                      <FormItem className="w-full sm:max-w-72">
                        <FormLabel className="sr-only">
                          {t("labels.service-number")}
                        </FormLabel>
                        <Popover
                          onOpenChange={(open) => {
                            setIsMsisdnNumberCommandOpen(open);
                          }}
                          open={isMsisdnNumberCommandOpen}
                        >
                          <PopoverTrigger asChild>
                            <FormControl>
                              <InputGroup>
                                <InputGroupInput
                                  className="w-full"
                                  onChange={(e) => {
                                    if (isMsisdnNumberCommandOpen) {
                                      setMsisdnNumberQuery(e.target.value);
                                    }
                                  }}
                                  onClick={() => {
                                    if (!isMsisdnNumberCommandOpen) {
                                      setIsMsisdnNumberCommandOpen(true);
                                    }
                                  }}
                                  placeholder={`${t(
                                    "placeholders.service-number"
                                  )}*`}
                                  readOnly={!isMsisdnNumberCommandOpen}
                                  value={
                                    isMsisdnNumberCommandOpen
                                      ? msisdnNumberQuery
                                      : displayValue
                                  }
                                />
                                <InputGroupAddon align="inline-end">
                                  {isMsisdnNumberCommandOpen ? (
                                    <SearchIcon />
                                  ) : (
                                    <ChevronDownIcon />
                                  )}
                                </InputGroupAddon>
                              </InputGroup>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent
                            align="start"
                            className="max-h-64 max-w-72 overflow-y-auto overflow-x-hidden p-0"
                            onOpenAutoFocus={(e) => e.preventDefault()}
                          >
                            {currentAvailableMSISDNs &&
                            currentAvailableMSISDNs.length > 0 ? (
                              currentAvailableMSISDNs.map((msisdn) => (
                                <Button
                                  className="flex h-auto w-full items-center justify-start px-3 py-2 text-left"
                                  key={msisdn.msisdnId}
                                  onClick={() => {
                                    field.onChange(msisdn.msisdnNo);
                                    form.setValue("msisdnId", msisdn.msisdnId);
                                    setIsMsisdnNumberCommandOpen(false);
                                  }}
                                  variant="ghost"
                                >
                                  <span className="truncate">
                                    {msisdn.msisdnNo}
                                  </span>
                                </Button>
                              ))
                              // biome-ignore lint/style/noNestedTernary: Necessary
                            ) : msisdnNumberQuery ? (
                              <div className="p-4 text-center text-muted-foreground text-sm">
                                {t("placeholders.no-results")}
                              </div>
                            ) : (
                              <div className="p-4 text-center text-muted-foreground text-sm">
                                {t("placeholders.search-number")}
                              </div>
                            )}
                          </PopoverContent>
                        </Popover>
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />
              </div>

              {session && (
                <>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end sm:gap-6">
                    <FormField
                      control={form.control}
                      name="imsiNumber"
                      render={({ field }) => (
                        <FormItem className="w-full sm:max-w-72">
                          <FormLabel className="sr-only">
                            {t("labels.imsi-number")}
                          </FormLabel>
                          <FormControl>
                            <ButtonGroup className="w-full">
                              <Input
                                maxLength={15}
                                placeholder={`${t("labels.imsi-number")}*`}
                                {...field}
                              />
                              <Button
                                onClick={() => setIsScannerOpen(true)}
                                type="button"
                                variant="outline"
                              >
                                <ScanLineIcon />
                              </Button>
                            </ButtonGroup>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Scanner Modal */}
                  <ScannerModal
                    isOpen={isScannerOpen}
                    onOpenChange={closeScannerModal}
                    onScanError={handleScanError}
                    onScanResult={handleScanResult}
                    stopStream={stopStream}
                  />
                </>
              )}
            </TimelineContent>
          </TimelineItem>
        </Timeline>

        <Separator />

        <div className="flex items-center justify-between gap-6">
          <Button onClick={() => navigate(-1)} type="button" variant="outline">
            <ChevronLeftIcon />
            <span>{t("labels.back")}</span>
          </Button>
          <SubmitButton
            className="rounded-md"
            isSubmitting={form.formState.isSubmitting}
          >
            <span>{t("labels.next")}</span>
            <ChevronRightIcon />
          </SubmitButton>
        </div>
      </form>
    </Form>
  );
};
